import type { IApiTransport } from "../definitions/IApiTransport";
import { saveNetworkResult } from "../helpers/SaveNetworkCalls";
import { looksLikeCsv } from "./parseNumericCsv";

/** Default `fetch` transport — all API calls go through this. */
class ApiTransport implements IApiTransport {
  getOptions(method: string | undefined, headers?: { [key: string]: string }): RequestInit {
    return {
      method: method || "GET",
      cache: "no-cache",
      credentials: "omit",
      referrerPolicy: "no-referrer",
      // No default `Content-Type` here (unlike `postOptions`) — a GET
      // carries no body for it to describe, and setting one anyway turns an
      // otherwise CORS-simple request into a preflighted one. Several data
      // sources this hits (e.g. the latency chart's CSV export) are plain
      // static file hosts that answer normal cross-origin GETs fine but
      // don't implement CORS preflight (`OPTIONS`) at all, so the added
      // header made every such request fail with a CORS error before it
      // ever reached the server. `headers` (only passed where a caller
      // genuinely needs one, e.g. `x-lmax-chartkey`) is still merged in
      // as-is — those already require preflight support server-side.
      headers: { ...(headers || {}) },
    };
  }

  postOptions(
    method: string | undefined,
    params: { body?: unknown },
    headers?: { [key: string]: string },
  ): RequestInit {
    const options: RequestInit = {
      method: method || "POST",
      cache: "no-cache",
      credentials: "omit",
      referrerPolicy: "no-referrer",
      headers: {
        "Content-Type": "application/json",
        ...(headers || {}),
      },
      body: "{}",
    };

    if (params?.body) {
      options.body = JSON.stringify(params.body);
    }

    return options;
  }

  makeAPICall<T>(url: string, requestObject?: RequestInit): Promise<T> {
    const config: RequestInit = { ...requestObject };
    let amendedUrl = url;

    // GET requests carrying a `body` (e.g. built via getOptions/postOptions)
    // get their payload folded into a querystring instead — `fetch` doesn't
    // allow a body on GET.
    if ((config.method || "GET") === "GET" && config.body) {
      const bodyObj =
        typeof config.body === "string" ? JSON.parse(config.body) : config.body;
      const query = new URLSearchParams(bodyObj as Record<string, string>);
      amendedUrl = `${url}?${query.toString()}`;
      delete config.body;
    }

    return fetch(amendedUrl, config)
      .then((response) => {
        if (!response.ok) {
          throw new Error(response.statusText);
        }
        // Some data sources (e.g. the latency chart's `.csv` exports) serve
        // CSV instead of JSON — sniffed from the response's `content-type`,
        // falling back to the URL's own extension for servers that don't
        // set it correctly. This layer only decides *how to read the body*
        // (text vs. JSON) — turning that CSV text into a domain shape like
        // `LatencyPoint`/`SpreadPoint` is each caller's own job (see
        // `parseNumericCsv` usage in `LatencyDataService`/`SpreadDataService`),
        // so a transport with no chart-specific knowledge stays reusable for
        // whatever else fetches through it.
        if (looksLikeCsv(amendedUrl, response.headers.get("content-type"))) {
          return response.text() as unknown as Promise<T>;
        }
        return response.json() as Promise<T>;
      })
      .then((data) => {
        saveNetworkResult("apiCall", "networkCall", url, config, data);
        return data;
      });
  }
}

export { ApiTransport };
